import React, { useState, useEffect } from 'react';
import { LinkedinOutlined, MailOutlined } from '@ant-design/icons';
import Banner from '../../assets/Illustration/about-banner.jpg';

import AboutIllustration from '../../assets/Illustration/ourVision.jpg';
import MissionImage from '../../assets/OtherImages/misson.png';
import DarkVision from '../../assets/OtherImages/dark-vision.png';
import LightVision from '../../assets/OtherImages/light-vision.png';
import PulakImage from '../../assets/Images/Pulak.png';
import ArunImage from '../../assets/Images/Arun.png';
import Vaibhavanand from '../../assets/Images/Vaibhav.jpg';
import Connect from '../products/EdgeGateway/connect';

function useDarkMode() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Check if dark class is on html element (Tailwind approach)
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    checkDarkMode();

    // Create observer to watch for class changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

const AboutUs = () => {
  const [scrollY, setScrollY] = useState(0);
  const isDarkMode = useDarkMode();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const founders = [
    {
      name: 'Pulak Rijhwani',
      image: PulakImage,
      linkedin: 'https://www.linkedin.com/in/pulak-rijhwani-65180a56',
      email: 'pulakrijhwani@opsight.ai',
    },
    {
      name: 'Arun Kumar',
      image: ArunImage,
      linkedin: 'https://www.linkedin.com/in/arun-kumar-4b025015a',
      email: 'arunkumar@opsight.ai',
    },
    {
      name: 'Vaibhav Anand',
      image: Vaibhavanand,
      linkedin: 'https://www.linkedin.com/in/vaibhav-anand-va/',
      email: 'vaibhavanand@opsight.ai',
    },
  ];

  return (
    <div className='bg-gray-50 dark:bg-gray-900 transition-colors duration-500 overflow-hidden'>
      {/* Floating Background */}
      <div className='fixed inset-0 pointer-events-none overflow-hidden'>
        <div
          className='absolute top-20 right-20 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl'
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div
          className='absolute bottom-20 left-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl'
          style={{ transform: `translateY(${-scrollY * 0.15}px)` }}
        />
      </div>

      {/* Hero Section */}
      <section className='relative min-h-screen flex items-center justify-center px-6'>
        <img
          src={Banner}
          alt='Opsight AI'
          className='absolute inset-0 w-full h-full object-cover opacity-50 dark:opacity-20'
        />
        <div className='absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/90 dark:from-gray-900/80 dark:via-gray-900/70 dark:to-gray-900/80' />

        <div className='relative z-10 max-w-6xl text-center'>
          <h1 className=' text-4xl sm:text-6xl text-nowrap md:text-8xl font-bold text-gray-900 dark:text-white mb-8'>
            OPSIGHT AI
          </h1>

          <p className='text-xl md:text-2xl lg:text-3xl text-gray-700 dark:text-gray-300 leading-relaxed'>
            Opsight AI Pvt Ltd is an Industrial-AIoT, SaaS Startup
            revolutionizing the way manufacturing companies harness data. We
            enable manufacturers to make smarter decisions, optimize operations
            & unlock their full manufacturing potential through intelligent data
            analytics.
          </p>
        </div>
      </section>

      {/* Vision */}
      <section className='py-32 px-6 bg-white dark:bg-gray-800'>
        <div className='max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center'>
          <img
            src={isDarkMode ? DarkVision : LightVision}
            alt='Our Vision'
            className='rounded-xl shadow-2xl'
          />
          <div>
            <h2 className=' text-4xl sm:text-6xl  font-bold text-gray-900 dark:text-white mb-6'>
              Our Vision
            </h2>
            <p className='text-2xl text-gray-700 dark:text-gray-300'>
              Enabling Indian manufacturers to achieve operational excellence &
              shift towards data-driven operations.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className='py-32 px-6 bg-gray-50 dark:bg-gray-900'>
        <div className='max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center'>
          <div>
            <h2 className=' text-4xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-6'>
              Our Mission
            </h2>
            <p className='text-2xl text-gray-700 dark:text-gray-300'>
              Empowering manufacturers to leverage IIoT & AI/ML to make informed
              decisions through predictive insights & address their biggest
              operational concerns:{' '}
              <b className='text-blue-600 dark:text-blue-400'>
                Equipment Downtime & Rework Costs.
              </b>
            </p>
          </div>

          <img
            src={MissionImage}
            alt='Our Mission'
            className='rounded-xl shadow-2xl'
          />
        </div>
      </section>

      {/* Founders */}
      <section className='py-32 px-6 bg-white dark:bg-gray-800'>
        <div className='max-w-7xl mx-auto'>
          <h2 className=' text-4xl sm:text-6xl font-bold text-center text-gray-900 dark:text-white mb-20'>
            Meet Our Founders
          </h2>

          <div className='grid md:grid-cols-3 gap-12'>
            {founders.map((founder, index) => (
              <div
                key={index}
                className='bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500'
              >
                <img
                  src={founder.image}
                  alt={founder.name}
                  className='w-32 h-32 rounded-full mx-auto object-cover mb-6'
                />

                <h3 className='text-2xl font-bold text-center text-gray-900 dark:text-white mb-6'>
                  {founder.name}
                </h3>

                <div className='flex justify-center gap-6'>
                  <a
                    href={founder.linkedin}
                    target='_blank'
                    rel='noreferrer'
                    className='w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 dark:text-white hover:bg-blue-500 text-gray-600 hover:text-white transition-all'
                  >
                    <LinkedinOutlined />
                  </a>
                  <a
                    href={`mailto:${founder.email}`}
                    className='w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 dark:text-white dark:bg-gray-700 hover:bg-teal-500 text-gray-600 hover:text-white transition-all'
                  >
                    <MailOutlined />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section>
        <Connect />
      </section>
    </div>
  );
};

export default AboutUs;
